import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, ExternalLink, LockKeyhole, MessageSquare } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { PostTree } from "@/components/rounds/PostTree";
import { ReplyComposer } from "@/components/rounds/ReplyComposer";
import { ReportMenu } from "@/components/rounds/ReportMenu";
import { RoleChip } from "@/components/rounds/ThreadRow";
import { VoteButton } from "@/components/rounds/VoteButton";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import { useBoardMe } from "@/lib/board";
import { MarkdownLite } from "@/lib/markdown-lite";
import {
  canEditRoundContent,
  formatRoundsTime,
  isIdentifierGuardError,
  roundsErrorMessage,
  useEditRoundThread,
  useRoundsStatus,
  useRoundThread,
} from "@/lib/rounds";

export default function RoundsThread() {
  const { city } = useCity();
  const { id = "" } = useParams<{ id: string }>();
  const detail = useRoundThread(city.slug, id);
  const member = useBoardMe(city.slug);
  const status = useRoundsStatus(city.slug);
  const editThread = useEditRoundThread(city.slug, id);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editBody, setEditBody] = useState("");
  const trackedThread = useRef<string | null>(null);

  useEffect(() => {
    if (!detail.data || trackedThread.current === detail.data.thread.id) return;
    trackedThread.current = detail.data.thread.id;
    track("thread_view", { thread_id: detail.data.thread.id });
  }, [detail.data]);

  useEffect(() => {
    if (!detail.data) return;
    setEditTitle(detail.data.thread.title);
    setEditBody(detail.data.thread.body);
  }, [detail.data]);

  if (detail.isLoading) return <div className="pb-rounds-state" role="status">Loading thread…</div>;
  if (detail.isError || !detail.data) {
    return (
      <div className="pb-rounds-state pb-rounds-state--error" role="alert">
        <span>The thread could not be loaded.</span>
        <button type="button" onClick={() => void detail.refetch()}>Retry</button>
      </div>
    );
  }

  const { thread, posts } = detail.data;
  const locked = thread.status === "locked";
  const canEdit =
    thread.status === "published" &&
    canEditRoundContent(thread.createdAt, thread.author.handle, member.data?.handle);
  const identifierError = isIdentifierGuardError(editThread.error);
  const specialist = specialistFromAskTitle(thread.title);

  const submitEdit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editTitle.trim() || !editBody.trim()) return;
    editThread.mutate(
      { title: editTitle.trim(), body: editBody.trim() },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <article className="pb-rounds-thread-page">
      <Link className="pb-rounds-back" to={`/board/rounds/c/${encodeURIComponent(thread.communitySlug)}`}>
        <ArrowLeft size={13} aria-hidden="true" />{thread.communityName}
      </Link>

      <section className="pb-rounds-thread-card">
        <header>
          <div className="pb-rounds-thread-row__chips">
            <Link to={`/board/rounds/c/${encodeURIComponent(thread.communitySlug)}`}>{thread.communityName}</Link>
            {thread.isAnonymisedCase ? <span>Anonymised case</span> : null}
            {locked ? <span><LockKeyhole size={10} aria-hidden="true" />Locked</span> : null}
          </div>

          {editing ? (
            <form className="pb-rounds-edit-form pb-rounds-edit-form--thread" onSubmit={submitEdit}>
              <label>
                Title
                <input
                  required
                  maxLength={200}
                  value={editTitle}
                  onChange={(event) => setEditTitle(event.target.value)}
                />
              </label>
              <label>
                Body
                <textarea
                  required
                  maxLength={8000}
                  value={editBody}
                  onChange={(event) => {
                    setEditBody(event.target.value);
                    if (editThread.isError) editThread.reset();
                  }}
                />
              </label>
              {identifierError ? (
                <p className="pb-rounds-identifier-error" role="alert">
                  Remove patient-identifying details before saving. <Link to="/board/rounds/guidelines">Read the case-writing guidelines.</Link>
                </p>
              ) : editThread.isError ? (
                <p className="pb-rounds-form-error" role="alert">{roundsErrorMessage(editThread.error)}</p>
              ) : null}
              <footer>
                <span>{editBody.length.toLocaleString("en-AU")} / 8,000</span>
                <div>
                  <button type="button" className="pb-rounds-plain-button" onClick={() => setEditing(false)}>Cancel</button>
                  <button className="pb-rounds-primary" type="submit" disabled={editThread.isPending || !editTitle.trim() || !editBody.trim()}>
                    {editThread.isPending ? "Saving…" : "Save edit"}
                  </button>
                </div>
              </footer>
            </form>
          ) : (
            <>
              <h1>{thread.title}</h1>
              <div className="pb-rounds-thread-byline">
                <span className="pb-rounds-author">
                  <strong>{thread.author.display}</strong>
                  <small>@{thread.author.handle}</small>
                  <RoleChip role={thread.author.role} />
                  {thread.author.userId ? (
                    <Link className="pb-rounds-message-link" to={`/board/messages/new?handle=${encodeURIComponent(thread.author.handle)}`}>
                      <MessageSquare size={11} aria-hidden="true" />Message
                    </Link>
                  ) : null}
                </span>
                <time dateTime={thread.createdAt}>{formatRoundsTime(thread.createdAt)}</time>
                <span>{thread.viewCount} views</span>
              </div>
            </>
          )}
        </header>

        {!editing ? <MarkdownLite>{thread.body}</MarkdownLite> : null}

        {thread.tags.length ? (
          <div className="pb-rounds-tags" aria-label="Thread tags">
            {thread.tags.map((tag) => <span key={tag}>{tag}</span>)}
          </div>
        ) : null}

        {thread.teachingTile ? (
          <aside className="pb-rounds-teaching-cta">
            <div>
              <strong>{thread.teachingTile.title}</strong>
              <span>This discussion started from a 5-Minute teaching session.</span>
            </div>
            <Link to="/board/referrals">
              Refer to {specialist ?? "this specialist"}<ExternalLink size={13} aria-hidden="true" />
            </Link>
          </aside>
        ) : null}

        <footer className="pb-rounds-thread-actions">
          <VoteButton
            citySlug={city.slug}
            threadId={thread.id}
            targetType="thread"
            targetId={thread.id}
            score={thread.score}
            myVote={thread.myVote}
          />
          <span><MessageSquare size={13} aria-hidden="true" />{thread.replyCount} replies</span>
          {canEdit && !editing ? <button type="button" onClick={() => setEditing(true)}>Edit</button> : null}
          <ReportMenu citySlug={city.slug} targetType="thread" targetId={thread.id} />
        </footer>

        {locked ? (
          <p className="pb-rounds-locked-notice">
            <LockKeyhole size={14} aria-hidden="true" />
            This thread is locked{thread.lockedReason ? `: ${thread.lockedReason}` : "."}
          </p>
        ) : null}
      </section>

      <section className="pb-rounds-replies" aria-labelledby="rounds-replies-heading">
        <header>
          <h2 id="rounds-replies-heading">Replies</h2>
          <span>{posts.length}</span>
        </header>
        <PostTree
          citySlug={city.slug}
          threadId={thread.id}
          posts={posts}
          currentHandle={member.data?.handle}
          locked={locked}
        />
      </section>

      {!locked ? (
        <section className="pb-rounds-root-reply" aria-labelledby="rounds-your-reply-heading">
          <h2 id="rounds-your-reply-heading">Add your reply</h2>
          {status.data && !status.data.handleSet ? (
            <p className="pb-rounds-setup-note" role="alert">
              Add your member handle in <Link to="/board/settings">Board settings</Link> before replying.
            </p>
          ) : (
            <ReplyComposer citySlug={city.slug} threadId={thread.id} />
          )}
        </section>
      ) : null}
    </article>
  );
}

function specialistFromAskTitle(title: string): string | null {
  const match = /^Ask\s+(.+?)\s+[—-]\s+/.exec(title);
  return match?.[1]?.trim() || null;
}
